import { ReactNode, useCallback, useRef, useState } from 'react'
import { useDelayedToggle } from '@/utils'
import { Typography } from '../../atoms'
import {
  CollapseBody,
  CollapseHeader,
  CollapseIcon,
  CollapsibleContainer,
} from './styles'

export interface CollapseProps {
  title: ReactNode
  children?: ReactNode
  isOpen?: boolean
  onCollapse?: () => void
  divide?: boolean
  showIcon?: boolean
}

interface CollapsibleProps {
  isOpen?: boolean
  children?: ReactNode
  destroyOnClose?: boolean
}

export const Collapse = ({
  title,
  children,
  isOpen = false,
  onCollapse,
  divide,
  showIcon = true,
}: CollapseProps) => {
  const [height, setHeight] = useState(0)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const childRef = useCallback((node: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect()
    resizeObserverRef.current = null
    if (!node) return
    const resizeObserver = new ResizeObserver(() => {
      setHeight(node.offsetHeight)
    })
    resizeObserver.observe(node)
    resizeObserverRef.current = resizeObserver
  }, [])

  return (
    <>
      <CollapseHeader onClick={onCollapse}>
        <Typography variant="captionB">{title}</Typography>
        {showIcon && <CollapseIcon $isOpen={isOpen} />}
      </CollapseHeader>
      <CollapseBody divide={divide} height={height || 0} isOpen={isOpen}>
        <div ref={childRef}>{children}</div>
      </CollapseBody>
    </>
  )
}

export const Collapsible = ({
  isOpen = false,
  children,
  destroyOnClose = true,
}: CollapsibleProps) => {
  const [height, setHeight] = useState(0)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const childRef = useCallback((node: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect()
    resizeObserverRef.current = null
    if (!node) return
    const resizeObserver = new ResizeObserver(() => {
      setHeight(node.offsetHeight)
    })
    resizeObserver.observe(node)
    resizeObserverRef.current = resizeObserver
  }, [])
  const renderChildren = useDelayedToggle({ isOpen, closingTime: 400 })

  return (
    <CollapsibleContainer height={height} isOpen={isOpen}>
      {(renderChildren || !destroyOnClose) && (
        <div ref={childRef}>{children}</div>
      )}
    </CollapsibleContainer>
  )
}
